import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, useMediaQuery } from '@mui/material';
import theme from './theme'; // Import the custom theme
import './App.css'; // Import global CSS

// Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import DJDashboard from './components/dj/DJDashboard';
import VenueDashboard from './components/venue/VenueDashboard';
import Navbar from './components/layout/Navbar';
import SubscriptionPlans from './components/subscription/SubscriptionPlans';
import SubscriptionSuccess from './components/subscription/SubscriptionSuccess';
import SubscriptionCancel from './components/subscription/SubscriptionCancel';
import AdminServers from './components/admin/AdminServers';
import AdminButtConfig from './components/admin/AdminButtConfig';
import AdminVenues from './components/admin/AdminVenues';
import HomePage from './components/home/HomePage';
import RetailPlayerSupportPage from './components/venue/RetailPlayerSupportPage';
import VenuesPage from './components/marketing/VenuesPage';
import DJsPage from './components/marketing/DJsPage';
import PricingPage from './components/marketing/PricingPage';
import AboutPage from './components/marketing/AboutPage';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Role-based Route component
const RoleRoute = ({ children, allowedRoles }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!allowedRoles.includes(user.userType)) {
    return <Navigate to="/" />;
  }

  return <ProtectedRoute>{children}</ProtectedRoute>;
};

// Admin Route component
const AdminRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Check if user exists, is authenticated (via ProtectedRoute), and is an admin
  if (!user?.isAdmin) {
    // Redirect non-admins away (e.g., to their regular dashboard or home)
    // Determine appropriate redirect based on user type if needed
    const redirectPath = user?.userType === 'dj' ? '/dj/dashboard' : '/venue/dashboard';
    return <Navigate to={redirectPath || "/"} />;
  }

  return <ProtectedRoute>{children}</ProtectedRoute>; 
};

// Responsive layout wrapper component
const ResponsiveLayout = ({ children }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Box
      sx={{
        flexGrow: 1,
        width: '100%',
        px: { xs: 2, sm: 0 },
        pb: { xs: 4, sm: 6 },
        overflowX: 'hidden',
      }}
    >
      {children}
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Navbar />
          <ResponsiveLayout>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              
              {/* DJ Routes */}
              <Route
                path="/dj/dashboard"
                element={
                  <RoleRoute allowedRoles={['dj']}>
                    <DJDashboard />
                  </RoleRoute>
                }
              />
              
              {/* Venue Routes */}
              <Route
                path="/venue/dashboard"
                element={
                  <RoleRoute allowedRoles={['venue']}>
                    <VenueDashboard />
                  </RoleRoute>
                }
              />
              
              {/* RetailPlayer Support Page */}
              <Route
                path="/venue/support/retailplayer"
                element={
                  <RoleRoute allowedRoles={['venue']}>
                    <RetailPlayerSupportPage />
                  </RoleRoute>
                }
              />
              
              {/* Subscription Routes */}
              <Route
                path="/subscription/plans"
                element={
                  <RoleRoute allowedRoles={['venue']}>
                    <SubscriptionPlans />
                  </RoleRoute>
                }
              />
              <Route
                path="/subscription/success"
                element={
                  <ProtectedRoute>
                    <SubscriptionSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription/cancel"
                element={
                  <ProtectedRoute>
                    <SubscriptionCancel />
                  </ProtectedRoute>
                }
              />
              
              {/* Admin Routes */}
              <Route 
                path="/admin/servers"
                element={
                  <AdminRoute>
                    <AdminServers />
                  </AdminRoute>
                }
              />
              <Route 
                path="/admin/butt-config"
                element={
                  <AdminRoute>
                    <AdminButtConfig />
                  </AdminRoute>
                }
              />
              <Route 
                path="/admin/venues"
                element={
                  <AdminRoute>
                    <AdminVenues />
                  </AdminRoute>
                }
              />
              
              {/* Home Page */}
              <Route
                path="/"
                element={<HomePage />}
              />
              
              {/* Marketing Pages */}
              <Route path="/venues" element={<VenuesPage />} />
              <Route path="/djs" element={<DJsPage />} />
              {/* Pricing page hidden during founding phase */}
              {/* <Route path="/pricing" element={<PricingPage />} /> */}
              {/* About page hidden during founding phase */}
              {/* <Route path="/about" element={<AboutPage />} /> */}
            </Routes>
          </ResponsiveLayout>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
